import type { RefObject } from 'react';

import type { WalletTotpEnrollmentMaterial } from '@/features/wallet-mfa/wallet-mfa-types';
import { Button, type OtpInputHandle, Spinner, theme, Typography } from '@/shared/design-system';

import { DialogActions } from '../dialog-actions';
import { TotpSetupSteps } from '../two-factor-authentication/modules/setup/totp-setup-steps';
import { TotpSetupPhase } from '../two-factor-authentication/totp-management-types';
import {
  CenteredStatus,
  DialogContent,
  WalletTotpSetupContent,
} from './wallet-mfa-settings.styled';
import { WalletTotpBackupStep } from './wallet-totp-backup-step';
import { downloadWalletTotpKey } from './wallet-totp-key-file';

type WalletTotpSetupViewProps = {
  isMobile: boolean;
  pending: boolean;
  material: WalletTotpEnrollmentMaterial | null;
  code: string;
  keySaved: boolean;
  error: string | null;
  copiedAnnouncement: string;
  otpRef: RefObject<OtpInputHandle | null>;
  onCodeChange: (_code: string) => void;
  onKeySavedChange: (_keySaved: boolean) => void;
  onCopy: () => void;
  onRetry: () => void;
};

export const WalletTotpSetupView = ({
  isMobile,
  pending,
  material,
  code,
  keySaved,
  error,
  copiedAnnouncement,
  otpRef,
  onCodeChange,
  onKeySavedChange,
  onCopy,
  onRetry,
}: WalletTotpSetupViewProps) => {
  if (pending && !material) {
    return (
      <CenteredStatus role="status" aria-live="polite" aria-busy="true">
        <Spinner />
        <Typography variant="body3" color="inherit">
          Preparing your authenticator setup…
        </Typography>
      </CenteredStatus>
    );
  }

  if (!material) {
    return (
      <DialogContent>
        <Typography role="alert" variant="body3" color={theme.colors.text.error}>
          {error}
        </Typography>
        <DialogActions>
          <Button aria-label="Retry authenticator setup" variant="outline" onClick={onRetry}>
            Retry
          </Button>
        </DialogActions>
      </DialogContent>
    );
  }

  return (
    <WalletTotpSetupContent data-responsive-surface={isMobile ? 'bottom-sheet' : 'modal'}>
      <TotpSetupSteps
        setupUri={material.setupUri}
        sharedSecret={material.sharedSecret}
        code={code}
        copiedAnnouncement={copiedAnnouncement}
        error={error}
        phase={pending ? TotpSetupPhase.Activating : TotpSetupPhase.Verifying}
        onCodeChange={onCodeChange}
        onCopy={onCopy}
        onRetryActivation={() => undefined}
        otpRef={otpRef}
        backupStep={
          <WalletTotpBackupStep
            keySaved={keySaved}
            onKeySavedChange={onKeySavedChange}
            onCopy={onCopy}
            onDownload={() => downloadWalletTotpKey(material)}
          />
        }
        codeLocked={!keySaved}
      />
    </WalletTotpSetupContent>
  );
};
